import { AuditLog, GetOptions } from "@/core";
import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import { useQuery } from "@tanstack/react-query";

const databaseService = serviceHost.getDatabaseService();
const auditLogRepository =
  repositoryHost.getAuditLogRepository(databaseService);

type GetAuditLogsParams = GetOptions<AuditLog>;

export const useGetAuditLogs = (params?: GetAuditLogsParams) => {
  return useQuery({
    queryKey: ["audit-logs", "get", params],
    queryFn: async () => {
      return await auditLogRepository.getAll(params || {});
    },
  });
};

export const useGetAuditLogsByDocument = (documentId: string) => {
  return useQuery({
    queryKey: ["audit-logs", "document", documentId],
    queryFn: async () => {
      return await auditLogRepository.getAll({
        queryConstraints: [
          { field: "documentId", operator: "==", value: documentId },
        ],
        orderBy: {
          field: "createdAt",
          direction: "desc",
        },
      });
    },
    enabled: !!documentId,
  });
};

export const useGetAuditLogsByUser = (userId: string) => {
  return useQuery({
    queryKey: ["audit-logs", "user", userId],
    queryFn: async () => {
      return await auditLogRepository.getAll({
        queryConstraints: [
          // Firestore nested-field dot-path; not expressible as keyof AuditLog.
          { field: "user.id", operator: "==", value: userId },
        ] as unknown as GetOptions<AuditLog>["queryConstraints"],
        orderBy: {
          field: "createdAt",
          direction: "desc",
        },
      });
    },
    enabled: !!userId,
  });
};

export const useGetAuditLogsByDocumentIds = (documentIds: string[]) => {
  const constrainedDocumentIds = documentIds.filter((id) => id.length > 0).slice(0, 10);

  return useQuery({
    queryKey: ["audit-logs", "documents", constrainedDocumentIds],
    queryFn: async () => {
      return await auditLogRepository.getAll({
        queryConstraints: [
          { field: "documentId", operator: "in", value: constrainedDocumentIds },
        ],
        orderBy: {
          field: "createdAt",
          direction: "desc",
        },
        pagination: {
          limit: 100,
        },
      });
    },
    enabled: constrainedDocumentIds.length > 0,
  });
};

export const useGetAuditLogsByInventoryId = (inventoryId: string) => {
  return useQuery({
    queryKey: ["audit-logs", "inventory", inventoryId],
    queryFn: async () => {
      return await auditLogRepository.getAll({
        queryConstraints: [
          { field: "inventoryId", operator: "==", value: inventoryId },
        ],
        orderBy: {
          field: "createdAt",
          direction: "desc",
        },
        pagination: {
          limit: 100,
        },
      });
    },
    enabled: inventoryId.length > 0,
  });
};
